import Icon from "@ant-design/icons";
import { Row, Col, Typography, Divider, Tooltip } from "antd";
import { sumBy } from "lodash";
import { FormattedMessage } from "react-intl";

import {
  formatDate,
  formatMoneyAmount,
  formatPercent,
  roundUnit,
} from "../../../lib/formats";
import {
  InventoryOrderDetailsFragment,
  FinanceOrderDetailsFragment,
  MakeOptional,
  TaxPlanWithComponentsFragment,
  TaxValueFragment,
} from "../../../lib/graphql";
import { useCurrentUser, useItemSidebarContext } from "../../../lib/hooks";
import { TaxComponentLabel } from "../../finance/taxes/TaxComponentLabel";
import { Form, Rules, InputNumber } from "../../form";
import { Space } from "../Space";
import { TruckIcon } from "../../../lib/images";

interface OrderSummaryProps {
  disabled?: boolean;
  order:
    | InventoryOrderDetailsFragment
    | MakeOptional<FinanceOrderDetailsFragment, "currencyCode">;
  taxValues: TaxValueFragment[];
  subtotal: number;
  otherCosts?: number;
  itemsDiscount?: number;
  taxPlans?: Array<TaxPlanWithComponentsFragment | null | undefined>;
}

export function OrderSummary({
  order,
  disabled,
  subtotal,
  otherCosts,
  itemsDiscount = 0,
  taxValues = [],
  taxPlans = [],
}: OrderSummaryProps) {
  const { currentTenant } = useCurrentUser();
  const taxPlanEnabled = !!currentTenant.features.taxPlan;

  if (!order.currency || !order.exchangeRate) return null;

  const subtotalExchanged = subtotal * order.exchangeRate;

  const discount = disabled
    ? order.discount
    : order.discountRate
      ? (order.discountRate * subtotal) / 100.0
      : 0;

  const other = otherCosts || 0;
  const tax = disabled
    ? order.tax
    : taxPlanEnabled
      ? sumBy(
          taxValues.filter((v) => v.priceApplication !== "inclusive"),
          "value"
        )
      : order.taxRate
        ? ((subtotal + other - discount) * order.taxRate) / 100.0
        : 0;

  const totalDiscount = itemsDiscount + discount;

  const total = disabled
    ? order.total
    : roundUnit(subtotal + other - discount + tax);

  const orderCurrencyCode = order.currencyCode || currentTenant.currencyCode;

  return (
    <Space vertical>
      <Row gutter={[0, 16]}>
        <Col span={8}></Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <FormattedMessage
            id="inventoryOrders.orderCurrency"
            defaultMessage="orderCurrency"
            values={{
              currency: orderCurrencyCode,
            }}
          />
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Typography.Text type="secondary">
            <FormattedMessage
              id="inventoryOrders.companyCurrency"
              defaultMessage="companyCurrency"
              values={{ currency: currentTenant.currencyCode }}
            />
          </Typography.Text>
        </Col>
      </Row>

      {itemsDiscount > 0 ? (
        <Row justify="space-between" gutter={[16, 16]}>
          <Col span={8}>
            <strong>
              <FormattedMessage
                id="itemsDiscount"
                defaultMessage="Items Discount"
              />
            </strong>
          </Col>
          <Col span={8} style={{ textAlign: "right" }}>
            {formatMoneyAmount(itemsDiscount, orderCurrencyCode)}
          </Col>
          <Col span={8} style={{ textAlign: "right" }}>
            <Typography.Text type="secondary">
              {formatMoneyAmount(
                itemsDiscount * order.exchangeRate,
                currentTenant.currencyCode
              )}
            </Typography.Text>
          </Col>
        </Row>
      ) : null}

      <Row justify="space-between" gutter={[16, 16]}>
        <Col span={8}>
          <strong>
            <FormattedMessage id="subtotal" />
          </strong>
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          {formatMoneyAmount(subtotal, orderCurrencyCode)}
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Typography.Text type="secondary">
            {formatMoneyAmount(subtotalExchanged, currentTenant.currencyCode)}
          </Typography.Text>
        </Col>
      </Row>

      <Row align="middle" gutter={[16, 16]}>
        <Col span={6}>
          <strong>
            <FormattedMessage id="invoice.discount" defaultMessage="discount" />
          </strong>
        </Col>
        <Col span={6}>
          {disabled ? (
            formatPercent(order.discountRate)
          ) : (
            <Form.Item compact name="discountRate" rules={[Rules.percentage]}>
              <InputNumber
                size="small"
                addonAfter="%"
                precision={2}
                min={0}
                max={99}
                style={{ maxWidth: "120px" }}
              />
            </Form.Item>
          )}
        </Col>
        <Col span={4} style={{ textAlign: "right" }}>
          <Typography.Text>
            {formatMoneyAmount(discount, orderCurrencyCode)}
          </Typography.Text>
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Typography.Text type="secondary">
            {formatMoneyAmount(
              discount * order.exchangeRate,
              currentTenant.currencyCode
            )}
          </Typography.Text>
        </Col>
      </Row>

      {otherCosts != undefined && (
        <Row align="middle" justify="space-between" gutter={[16, 16]}>
          <Col span={12}>
            <Space>
              <strong>
                <FormattedMessage
                  id="inventoryPurchaseOrders.other"
                  defaultMessage="freight"
                />
              </strong>

              <ExternalFreightButton
                order={order as InventoryOrderDetailsFragment}
                disabled={disabled}
              />
            </Space>
          </Col>
          <Col span={4} style={{ textAlign: "right" }}>
            {disabled ? (
              formatMoneyAmount(other, orderCurrencyCode)
            ) : (
              <Form.Item compact name="otherCosts" rules={[Rules.gtEqZero]}>
                <InputNumber
                  size="small"
                  min={0}
                  addonBefore={order.currency?.symbol}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            )}
          </Col>
          <Col span={8} style={{ textAlign: "right" }}>
            <Typography.Text type="secondary">
              {formatMoneyAmount(
                other * order.exchangeRate,
                currentTenant.currencyCode
              )}
            </Typography.Text>
          </Col>
        </Row>
      )}

      {taxPlanEnabled &&
        taxValues
          .filter((tv) =>
            ["invoiceHeader", "both"].includes(tv.visibilityScope)
          )
          .map((tv) => (
            <Row
              key={tv.component.id}
              justify="space-between"
              gutter={[16, 16]}
            >
              <Col span={8}>
                <Typography.Text strong>
                  <TaxComponentLabel
                    taxComponent={tv.component}
                    taxPlans={taxPlans}
                  />
                </Typography.Text>
              </Col>
              <Col span={8} style={{ textAlign: "right" }}>
                {formatMoneyAmount(tv.value, orderCurrencyCode)}
              </Col>
              <Col span={8} style={{ textAlign: "right" }}>
                <Typography.Text type="secondary">
                  {formatMoneyAmount(
                    (tv.value || 0) * order.exchangeRate,
                    currentTenant.currencyCode
                  )}
                </Typography.Text>
              </Col>
            </Row>
          ))}

      {!taxPlanEnabled || order.taxRate ? (
        <Row align="middle" gutter={[16, 16]}>
          <Col span={6}>
            <strong>
              <FormattedMessage id="vat" defaultMessage="VAT" />
            </strong>
          </Col>
          <Col span={4}>
            {disabled ? (
              formatPercent(order.taxRate)
            ) : (
              <Form.Item compact name="taxRate" rules={[Rules.percentage]}>
                <InputNumber
                  size="small"
                  addonAfter="%"
                  min={0}
                  max={100}
                  style={{ minWidth: "90px", marginLeft: "16px" }}
                />
              </Form.Item>
            )}
          </Col>
          <Col span={6} style={{ textAlign: "right" }}>
            {formatMoneyAmount(tax, orderCurrencyCode)}
          </Col>
          <Col span={8} style={{ textAlign: "right" }}>
            <Typography.Text type="secondary">
              {formatMoneyAmount(
                tax * order.exchangeRate,
                currentTenant.currencyCode
              )}
            </Typography.Text>
          </Col>
        </Row>
      ) : null}

      <Divider />

      {totalDiscount > 0 ? (
        <Row align="middle" justify="space-between">
          <Col span={8}>
            <strong>
              <FormattedMessage
                id="totalDiscount"
                defaultMessage="Total Discount"
              />
            </strong>
          </Col>
          <Col span={8} style={{ textAlign: "right" }}>
            <Typography.Text style={{ fontWeight: 500 }}>
              {formatMoneyAmount(totalDiscount, orderCurrencyCode)}
            </Typography.Text>
          </Col>
          <Col span={8} style={{ textAlign: "right" }}>
            <Typography.Text type="secondary">
              {formatMoneyAmount(
                totalDiscount * order.exchangeRate,
                currentTenant.currencyCode
              )}
            </Typography.Text>
          </Col>
        </Row>
      ) : null}

      <Row align="middle" justify="space-between">
        <Col span={8}>
          <strong>
            <FormattedMessage id="grandTotal" defaultMessage="Grand Total" />
          </strong>
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Typography.Text style={{ fontWeight: 500 }}>
            {formatMoneyAmount(total, orderCurrencyCode)}
          </Typography.Text>
        </Col>
        <Col span={8} style={{ textAlign: "right" }}>
          <Typography.Text type="secondary">
            {formatMoneyAmount(
              total * order.exchangeRate,
              currentTenant.currencyCode
            )}
          </Typography.Text>
        </Col>
      </Row>
    </Space>
  );
}

function ExternalFreightButton({
  order,
  disabled,
}: {
  order: InventoryOrderDetailsFragment;
  disabled?: boolean;
}) {
  const { setCurrentAction } = useItemSidebarContext();

  if (disabled && !order.freightInvoice) return null;

  return (
    <Tooltip
      title={
        disabled ? (
          <Space orientation="vertical">
            <div>
              <FormattedMessage id="counterparties.entityName" />:{" "}
              {order.freightInvoice?.counterparty?.name}
            </div>
            <div>
              <FormattedMessage id="paymentMethods.entityName" />:{" "}
              {order.freightInvoice?.paymentMethod?.name}
            </div>
            <div>
              <FormattedMessage id="internalId" />:{" "}
              {order.freightInvoice?.internalId}
            </div>
            <div>
              <FormattedMessage id="documentDate" />:{" "}
              {formatDate(order.freightInvoice?.documentDate)}
            </div>
          </Space>
        ) : (
          <FormattedMessage
            id="inventoryPurchaseOrders.addExternalFreight"
            defaultMessage="Add External Freight"
          />
        )
      }
    >
      <Icon
        style={{ cursor: "pointer" }}
        component={() => <TruckIcon width={22} />}
        onClick={() => setCurrentAction("addExternalFreight")}
      />
    </Tooltip>
  );
}
